import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Download, RefreshCw, Mail, CheckCircle2 } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const TOKEN_KEY = "sarathix_admin_token";

const STATUS_LABELS = {
  active: { label: "Active", cls: "bg-amber-500/15 text-amber-400 border-amber-500/30" },
  notified: { label: "Notified", cls: "bg-blue-500/15 text-blue-400 border-blue-500/30" },
  converted: { label: "Converted", cls: "bg-emerald-500/15 text-emerald-400 border-emerald-500/30" },
};

export default function AdminWaitlist() {
  const [entries, setEntries] = useState([]);
  const [totals, setTotals] = useState({ active: 0, notified: 0, converted: 0, all: 0 });
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState("");

  const authHeader = useCallback(() => {
    const token = sessionStorage.getItem(TOKEN_KEY);
    return token ? { Authorization: `Bearer ${token}` } : {};
  }, []);

  const fetchEntries = useCallback(async () => {
    setLoading(true);
    try {
      const params = statusFilter ? { status: statusFilter } : {};
      const resp = await axios.get(`${API}/admin/waitlist`, { headers: authHeader(), params });
      setEntries(resp.data?.entries || []);
      setTotals(resp.data?.totals || { active: 0, notified: 0, converted: 0, all: 0 });
    } catch (err) {
      toast.error("Couldn't load waitlist.");
    } finally {
      setLoading(false);
    }
  }, [statusFilter, authHeader]);

  useEffect(() => {
    fetchEntries();
  }, [fetchEntries]);

  const handleStatusChange = async (id, newStatus) => {
    try {
      await axios.post(`${API}/admin/waitlist/${id}/status`, { status: newStatus }, { headers: authHeader() });
      toast.success("Status updated.");
      fetchEntries();
    } catch {
      toast.error("Couldn't update status.");
    }
  };

  const handleExport = async () => {
    try {
      const token = sessionStorage.getItem(TOKEN_KEY);
      const resp = await fetch(`${API}/admin/waitlist/export.csv`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!resp.ok) throw new Error("Export failed");
      const blob = await resp.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `sarathix-waitlist-${new Date().toISOString().slice(0, 10)}.csv`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success("CSV downloaded.");
    } catch {
      toast.error("Couldn't download CSV.");
    }
  };

  const formatDate = (iso) => {
    if (!iso) return "—";
    const d = new Date(iso);
    return d.toLocaleString("en-IN", { dateStyle: "medium", timeStyle: "short" });
  };

  return (
    <div className="space-y-6" data-testid="admin-waitlist-page">
      {/* Header */}
      <div className="flex items-start justify-between flex-wrap gap-4">
        <div>
          <h1 className="font-outfit text-2xl font-bold text-white tracking-tight">Waitlist</h1>
          <p className="text-sarathi-text-secondary text-sm mt-1">
            People interested in city rides, captured during the beta phase.
          </p>
        </div>
        <div className="flex gap-2">
          <button
            type="button"
            onClick={fetchEntries}
            className="inline-flex items-center gap-2 px-3 py-2 rounded-lg bg-white/5 border border-white/10 text-white text-sm hover:bg-white/10 transition-colors"
            data-testid="waitlist-refresh"
          >
            <RefreshCw className="w-4 h-4" />
            Refresh
          </button>
          <button
            type="button"
            onClick={handleExport}
            className="inline-flex items-center gap-2 px-3 py-2 rounded-lg bg-sarathi-primary hover:bg-orange-600 text-white text-sm font-semibold transition-colors"
            data-testid="waitlist-export"
          >
            <Download className="w-4 h-4" />
            Export CSV
          </button>
        </div>
      </div>

      {/* Totals */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        {[
          { key: "all", label: "Total" },
          { key: "active", label: "Active" },
          { key: "notified", label: "Notified" },
          { key: "converted", label: "Converted" },
        ].map((t) => (
          <div key={t.key} className="bg-sarathi-surface border border-white/10 rounded-xl p-4">
            <p className="text-[11px] uppercase tracking-wider text-sarathi-text-muted font-semibold">{t.label}</p>
            <p className="text-2xl font-bold text-white mt-1" data-testid={`waitlist-count-${t.key}`}>{totals[t.key] ?? 0}</p>
          </div>
        ))}
      </div>

      {/* Filter */}
      <div className="flex gap-2 items-center flex-wrap">
        <span className="text-xs text-sarathi-text-muted uppercase tracking-wider font-semibold">Filter:</span>
        {[
          { v: "", l: "All" },
          { v: "active", l: "Active" },
          { v: "notified", l: "Notified" },
          { v: "converted", l: "Converted" },
        ].map((f) => (
          <button
            type="button"
            key={f.v || "all"}
            onClick={() => setStatusFilter(f.v)}
            className={`px-3 py-1.5 rounded-full text-xs font-medium border transition-colors ${
              statusFilter === f.v
                ? "bg-sarathi-primary text-white border-sarathi-primary"
                : "bg-white/5 text-sarathi-text-secondary border-white/10 hover:bg-white/10"
            }`}
          >
            {f.l}
          </button>
        ))}
      </div>

      {/* Table */}
      <div className="bg-sarathi-surface border border-white/10 rounded-xl overflow-hidden">
        {loading ? (
          <div className="p-12 text-center text-sarathi-text-muted text-sm">Loading…</div>
        ) : entries.length === 0 ? (
          <div className="p-12 text-center">
            <Mail className="w-10 h-10 text-sarathi-text-muted mx-auto mb-3 opacity-40" />
            <p className="text-sarathi-text-secondary text-sm">No waitlist entries yet.</p>
            <p className="text-sarathi-text-muted text-xs mt-1">Entries appear as soon as someone signs up on the home page.</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-white/[0.03] border-b border-white/10 text-xs uppercase tracking-wider text-sarathi-text-muted">
                  <th className="text-left font-semibold px-4 py-3">Name</th>
                  <th className="text-left font-semibold px-4 py-3">Phone</th>
                  <th className="text-left font-semibold px-4 py-3">Service</th>
                  <th className="text-left font-semibold px-4 py-3">Joined</th>
                  <th className="text-left font-semibold px-4 py-3">Status</th>
                  <th className="text-right font-semibold px-4 py-3">Actions</th>
                </tr>
              </thead>
              <tbody>
                {entries.map((e) => {
                  const statusInfo = STATUS_LABELS[e.status] || STATUS_LABELS.active;
                  return (
                    <tr key={e.id} className="border-b border-white/5 last:border-0 hover:bg-white/[0.02] transition-colors">
                      <td className="px-4 py-3 text-white font-medium">{e.name}</td>
                      <td className="px-4 py-3 text-sarathi-text-secondary font-mono">
                        <a href={`tel:+91${e.phone}`} className="hover:text-sarathi-primary transition-colors">{e.phone}</a>
                      </td>
                      <td className="px-4 py-3 text-sarathi-text-secondary capitalize">{(e.service || "").replace("_", " ")}</td>
                      <td className="px-4 py-3 text-sarathi-text-muted text-xs whitespace-nowrap">{formatDate(e.created_at)}</td>
                      <td className="px-4 py-3">
                        <span className={`inline-block px-2 py-0.5 rounded-md text-[11px] font-semibold border ${statusInfo.cls}`}>
                          {statusInfo.label}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-right">
                        {e.status === "active" && (
                          <button
                            type="button"
                            onClick={() => handleStatusChange(e.id, "notified")}
                            className="inline-flex items-center gap-1 px-2.5 py-1 rounded-md bg-blue-500/10 border border-blue-500/30 text-blue-400 text-[11px] font-semibold hover:bg-blue-500/20 transition-colors"
                            data-testid={`mark-notified-${e.id}`}
                          >
                            <Mail className="w-3 h-3" />
                            Mark notified
                          </button>
                        )}
                        {e.status === "notified" && (
                          <button
                            type="button"
                            onClick={() => handleStatusChange(e.id, "converted")}
                            className="inline-flex items-center gap-1 px-2.5 py-1 rounded-md bg-emerald-500/10 border border-emerald-500/30 text-emerald-400 text-[11px] font-semibold hover:bg-emerald-500/20 transition-colors"
                          >
                            <CheckCircle2 className="w-3 h-3" />
                            Mark converted
                          </button>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
